import { CClient, flattenErrorsDictValues } from '@common/utils'
import {
  CELERY_TASK_STATUS,
  MAXMIND_GEOIP2_DB_FALLBACK_FILE_NAME,
  MAXMIND_GEOIP2_DB_FILE_NAME,
} from '@lib/constants/common'
import {
  CeleryTaskStatusResponseType,
  CheckIsDOMNodeChildOfOptionsType,
} from '@lib/types/common'
import { captureException, captureMessage } from '@sentry/node'
import { IncomingMessage } from 'http'
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import { NextApiRequestCookies } from 'next/dist/server/api-utils'
import Router from 'next/router'
import { parseCookies } from 'nookies'

export const redirectToForGetInitialProps = (ctx, redirectUrl) => {
  if (typeof window === 'undefined') {
    // This is running on server-side
    const finalRedirectUrl =
      ctx.locale !== ctx.defaultLocale
        ? `/${ctx.locale}${redirectUrl}`
        : redirectUrl
    ctx.res.writeHead(302, { location: finalRedirectUrl })
    ctx.res.end()
  } else {
    // This is running on client-side
    Router.push(redirectUrl)
  }
  return {}
}

export const getServerSideRedirectResult = (
  ctx: GetServerSidePropsContext,
  redirectUrl: string
): GetServerSidePropsResult<any> => {
  const finalRedirectUrl =
    typeof window === 'undefined' && ctx.locale !== ctx.defaultLocale
      ? `/${ctx.locale}${redirectUrl}`
      : redirectUrl

  return {
    redirect: {
      destination: finalRedirectUrl,
      permanent: false,
    },
  }
}

export const removeLocaleFromPathname = (pathname: string, locale: string) =>
  pathname?.split('/')?.[1] === locale ? pathname?.split(locale)[1] : pathname

export const isEmptyObject = (obj: Record<string, any>) => {
  return Object.keys(obj).length === 0
}

export const getSegmentUserType = (
  isPaidUser: boolean,
  isCompetitionUser: boolean,
  loggedIn = true
) => {
  if (!loggedIn) return 'none'
  if (isPaidUser) return 'paid'
  if (isCompetitionUser) return 'other'
  return 'trial'
}

export const convertToKebabCase = (input: string) =>
  input.toLowerCase().trim().split(' ').join('-')

/**
 * @param taskId Celery task id for which we want to know the status of
 * @param pollingInterval Unit: ms. Default value is 1000.
 * @param timeoutLimit Unit: ms. Default value is 120000.
 * @returns Returns true if the status is successful else false
 */
export const pollCeleryTaskStatus = async (
  taskId: string,
  pollingInterval = 1000,
  timeoutLimit = 120000
): Promise<boolean> => {
  try {
    const response = await CClient(`/api/v1/celery-tasks/${taskId}/`)
    if (!response.ok)
      throw new Error(`${response.status}: ${response.statusText}`)
    const data: CeleryTaskStatusResponseType = await response.json()

    if (CELERY_TASK_STATUS.SUCCESS.includes(data.status)) return true
    else if (CELERY_TASK_STATUS.FAILED.includes(data.status)) return false
    else if (timeoutLimit <= 0)
      throw new Error(
        `Polling timeout: Polling ran for ${timeoutLimit}ms with no result`
      )
    else {
      // Waiting for `pollingInterval` milliseconds before calling the function again.
      await new Promise((resolve) => setTimeout(resolve, pollingInterval))
      return await pollCeleryTaskStatus(
        taskId,
        pollingInterval,
        timeoutLimit - pollingInterval
      )
    }
  } catch (error) {
    console.error(error)
  }
  return false
}

/**
 * @param width Original width of the element (image)
 * @param height Original height of the element (image)
 * @param scale The scale factor to multiply with. Final Dimensions(w * scale, h * scale)
 * @returns
 */
export const getDimensions = (width: number, height: number, scale = 1) => ({
  width: width * scale,
  height: height * scale,
})

/** Extracts and returns error message from the response. If no error
 * message is present, returns status code.
 */
export const flattenErrorsDictFromResponse = async (response: Response) => {
  try {
    const errorData = await response?.json()

    // If we get a properly formatted error object, let's make it more readable
    if (errorData && typeof errorData === 'object') {
      const errors = flattenErrorsDictValues(errorData)

      // Format JSON objects for better display
      if (errors?.length > 0) {
        return errors.join('\n')
      } else {
        // If flattenErrorsDictValues didn't find anything but we have an object,
        // stringify it nicely for display
        return JSON.stringify(errorData, null, 2)
      }
    }

    return errorData || response?.status?.toString()
  } catch (error) {
    // If we can't parse JSON, return status code
    return response?.status?.toString()
  }
}

/**
 * @param {Response} response - The response from the API call.
 * @param {string} url - The URL that was requested.
 * @param [requestBody=null] - The request body that was sent to the API.
 * @returns A string that contains the error message, the url, the request body, and the auth token.
 */
export const getAPIErrorString = async (
  response: Response,
  url: string,
  requestBody: Record<string, any> | string = null
) => {
  let errorDetails = ''

  // Get error details from response
  if (response) {
    try {
      const errorData = await response.clone().json() // Use clone to avoid "body already read" errors

      // Format the error details nicely if it's an object
      if (errorData && typeof errorData === 'object') {
        // Create a more readable representation of the error
        const formattedErrors = []
        Object.entries(errorData).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            formattedErrors.push(`${key}: ${value.join(', ')}`)
          } else if (typeof value === 'object' && value !== null) {
            Object.entries(value as Record<string, any>).forEach(
              ([subKey, subValue]) => {
                if (Array.isArray(subValue)) {
                  formattedErrors.push(
                    `${key}.${subKey}: ${subValue.join(', ')}`
                  )
                } else {
                  formattedErrors.push(`${key}.${subKey}: ${subValue}`)
                }
              }
            )
          } else {
            formattedErrors.push(`${key}: ${value}`)
          }
        })

        if (formattedErrors.length > 0) {
          errorDetails = formattedErrors.join('; ')
        } else {
          errorDetails = JSON.stringify(errorData)
        }
      } else {
        // If not an object, use the flattened error dict approach as fallback
        errorDetails = await flattenErrorsDictFromResponse(response)
      }
    } catch (error) {
      // If we can't parse JSON, use flattened error dict
      errorDetails = await flattenErrorsDictFromResponse(response)
    }
  } else {
    errorDetails = 'No response!'
  }

  const requestBodyString = requestBody
    ? typeof requestBody === 'string'
      ? requestBody
      : JSON.stringify(requestBody)
    : 'NO REQUEST BODY'
  const token = parseCookies().token

  return `${errorDetails} | URL: ${url} | REQUEST_BODY: ${requestBodyString} | TOKEN: ${token}`
}

/**
 * It checks if a DOM node is a child of another DOM node whose id or className is given
 * @returns a boolean
 */
export const checkIsDOMNodeChildOf = ({
  node,
  parentClassName = null,
  parentId = null,
}: CheckIsDOMNodeChildOfOptionsType) => {
  const allParentNodeIds = []
  const allParentNodeClassNames = []

  let currNode = node

  while (currNode) {
    if (parentId && currNode.id) allParentNodeIds.push(currNode.id)
    if (parentClassName && currNode.className)
      allParentNodeClassNames.push(currNode.className)
    currNode = currNode.parentNode
  }

  return (
    allParentNodeIds.includes(parentId) ||
    allParentNodeClassNames.includes(parentClassName)
  )
}

export const formatCountryQuery = (countryCode: string) => {
  return countryCode ? `&country=${countryCode}` : ''
}

//get all indexes of a particular value in an array
export const getAllIndices = (arr, val) => {
  const indices = []
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] === val) indices.push(i)
  }
  return indices
}

//frequecy map of all the elements in an array
export const getFrequency = (array) => {
  const map = {}
  array.forEach((item) => {
    if (map[item]) {
      map[item]++
    } else {
      map[item] = 1
    }
  })
  return map
}

export const playSound = async (sound: HTMLAudioElement) => {
  try {
    await sound?.play()
  } catch (error) {
    console.error(error)
  }
}

export const getFirstNameAndLastName = (name: string) => {
  const indexOfSpace = name?.indexOf(' ')
  if (indexOfSpace === -1)
    return {
      firstName: name,
      lastName: '',
    }

  return {
    firstName: name?.substring(0, indexOfSpace).toLowerCase(),
    lastName: name?.substring(indexOfSpace + 1).toLowerCase(),
  }
}

export const parseIpFromReq = (
  req: IncomingMessage & {
    cookies: NextApiRequestCookies
  }
) => {
  let forwarded = req?.headers?.['x-forwarded-for']
  if (typeof forwarded !== 'string') forwarded = forwarded?.[0]
  return forwarded?.split(',').shift() || req.socket?.remoteAddress
}

const getMaxmindIpDatabaseFileName = async () => {
  const fs = await import('fs')

  let fileName = ''
  if (fs.existsSync(MAXMIND_GEOIP2_DB_FILE_NAME))
    fileName = MAXMIND_GEOIP2_DB_FILE_NAME
  else {
    fileName = MAXMIND_GEOIP2_DB_FALLBACK_FILE_NAME
    const message =
      'Latest Maxmind GeoIP2 database file not found. Using fallback.'
    captureMessage(message)
  }

  return fileName
}

export const getCountryFromIP = async (ctx): Promise<string | null> => {
  try {
    if (typeof window !== 'undefined') return null
    const { req } = ctx
    const ip = parseIpFromReq(req)
    if (!ip) return null
    const fileName = await getMaxmindIpDatabaseFileName()
    const maxmind = await import('@maxmind/geoip2-node')
    const reader = await maxmind.Reader.open(fileName)
    const countryData = reader.country(ip)
    return countryData?.country?.isoCode
  } catch (error) {
    if (error?.name === 'AddressNotFoundError') return null
    captureException(error)
  }
}

export const convertMarkdownToHtml = (text: string): string => {
  if (!text) return text
  return text.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
}

export const maskPhoneNumber = (phone: string | number) => {
  if (!phone) return phone
  if (typeof phone === 'number') phone = phone.toString()
  return phone?.replace(/(\d{4})(\d{4})$/, '****$2')
}

export const maskEmail = (email: string) => {
  if (!email) return email
  const [localPart, domain] = email.split('@')
  if (!domain) return email
  return `${localPart.slice(0, 2)}****@${domain}`
}

/**
 * Name the in-app browser a page is open in, from its user agent, or 'none'.
 *
 * Paid-social leads mostly open our links inside the Facebook or Instagram
 * app, whose WebView can report itself as Chrome. Telling them apart lets
 * analytics see whether a problem is specific to those WebViews.
 */
export const getInAppBrowser = (userAgent: string): string => {
  if (/Instagram/.test(userAgent)) return 'instagram'
  if (/FBAN|FBAV|FB_IAB|FBIOS/.test(userAgent)) return 'facebook'
  // Any other Android app embedding a WebView marks it with `; wv)`.
  if (/; wv\)/.test(userAgent)) return 'android_webview'
  return 'none'
}
